import './TrackingEvents.css';
import { useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import { DefaultOptionType } from "antd/es/select";
import EventCard from "./EventCard";
import { EventService } from "../../services/events.api.client";
import { Event, mapToCalendar, mapToEvent } from "../../models/calendar/Event.model";
import { useCalendar } from '../../contexts/calendar/CalendarContext.context';
import { useCompany } from '../../contexts/CompanyContext';
import { useContact } from '../../contexts/ContactContext';
import { useCalendars } from '../../contexts/calendar/CalendarsContext.context';
import { CalendarService } from '../../services/calendar.api.client';
import { Calendar } from '../../models/calendar/Calendar.model';


type Props = {
    day:string,
    month:string,
    year:string
}

export default function EventTracking({day,month,year}:Readonly<Props>){
    const [events,setEvents] = useState<Event[]>([]);
    const [userFilter, setUserFilter] = useState<string>('');
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set([]));
    const {calendar, setCalendar} = useCalendar();
    const {calendars, setCalendars} = useCalendars();
    const {company} = useCompany();
    const { contacts: allContacts, fetchContacts } = useContact();
    
    useEffect(() => {
      if (!company.current.uid) return;
      if (calendars.length === 0) {
        CalendarService.getCalendarByCompany(company.current.uid)
          .then((data: Calendar[]) => {
            if (Array.isArray(data)) {
              setCalendars(data.map(c => ({...c, ...mapToCalendar(c)})));
            }
          });
      }
    }, [company.current.uid, calendars.length, setCalendars]);

    useEffect(() => {
      if (!calendar.uid && calendars.length > 0) {
        const selected = calendars[0];
        const weekrules = [false, false, false, false, false, false, false].map((_, i) => (
          selected.weekAvailable.some((day: any) => day.numberDay === i)
        ));
        
        setCalendar((prev) => ({
          ...prev,
          id: selected.id,
          uid: selected.uid,
          color: selected.color,
          availableWeekRules: weekrules,
          weekAvailable: selected.weekAvailable,
          customEvents: selected.customEvents
        }));
      }
    }, [calendars, calendar.uid, setCalendar]);
    
    const employees = useMemo(() => 
        allContacts.filter(c => c.role === 'EMPLOYEE'),
    [allContacts]);
    
    useEffect(()=>{
      if(!company.current.uid || !calendar.uid) return;
      const nowDate = new Date(Number.parseInt(year),Number.parseInt(month),Number.parseInt(day));
      const initDayWeek = `${nowDate.getFullYear()}-${(nowDate.getMonth()+1).toString().padStart(2,'0')}-${nowDate.getDate().toString().padStart(2,'0')}T00:00:00-05:00`;
      const endDayWeek =  `${nowDate.getFullYear()}-${(nowDate.getMonth()+1).toString().padStart(2,'0')}-${nowDate.getDate().toString().padStart(2,'0')}T23:59:00-05:00`;
      
      EventService.getEvents(company.current.uid,calendar.uid,initDayWeek,endDayWeek)
        .then((response)=>{
          const rawEvents: any[] = response;
          if (Array.isArray(rawEvents)) {
            const mappedEvents = rawEvents.map(e => mapToEvent(e) as Event);
            setEvents(mappedEvents);
          }
        });

      fetchContacts('EMPLOYEE');
    },[year,month,day,calendar.uid,company.current.uid]);

    useEffect(() => {
        if (employees.length > 0) {
            const keys = new Set(employees.map(emp => emp.uid));
            setSelectedKeys(keys);
            setUsersByEmployees(employees);
            setUserFilter(prev => prev || employees[0].uid);
        }
    }, [employees]);

    const [usersOptions, setUsersOptions] = useState<DefaultOptionType[]>([]);

    const setUsersByEmployees = (emps: any[]) => {
        const options = emps.map(emp => ({ label: emp.name, value: emp.uid }));
        setUsersOptions(options);
    }

    const morningEvents = useMemo(()=>events
      ?.filter(e => selectedKeys.has(e.responsibleId) && e.start && new Date(e.start).getHours() < 12)
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()),[selectedKeys,events]);
  
    const afternoonEvents = useMemo(()=> events
      ?.filter(e => selectedKeys.has(e.responsibleId) && e.start && new Date(e.start).getHours() >= 12)
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()),[selectedKeys,events]);

      
    return (
        <div className="planner-container">
          <Select
              className="mt-2 mb-4 w-[30%]"
              placeholder="Seleccione un Usuario"
              options={usersOptions}
              value={userFilter}
              onChange={(value)=>setUserFilter(value)}
              >
          </Select>
          <div className="planner-grid mt-4">
            <div className="planner-column">
              <h2 className="column-heading">Mañana</h2>
              <div className="events-list">
                {morningEvents.length > 0 ? (
                  morningEvents.filter((event)=>event.responsibleId === userFilter || !userFilter).map(event => <EventCard key={event.id} event={event} />)
                ) : <p className="no-events-placeholder">Sin eventos por la mañana.</p>}
              </div>
            </div>
            <div className="planner-column">
              <h2 className="column-heading">Tarde</h2>
              <div className="events-list">
                {afternoonEvents.length > 0 ? (
                  afternoonEvents.filter((event)=>event.responsibleId === userFilter || !userFilter).map(event => <EventCard key={event.id} event={event} />)
                ) : <p className="no-events-placeholder">Sin eventos por la tarde.</p>}

              </div>
            </div>
          </div>
        </div>
      );
  }



